"use client";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useTranslations } from "next-intl";

const SN4PE_URL = "https://sn4pe.com";
const TEXT = "sn4pe.com";
const SECTIONS = ["labs", "blog", "coffee", "trails", "dog", "projects"];

function rand(i: number, seed: number) {
    const x = Math.sin(i * 9.7 + seed * 17.3) * 10000;
    return x - Math.floor(x);
}

const REDUCE_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function subscribeReduceMotion(notify: () => void) {
    const mq = window.matchMedia(REDUCE_MOTION_QUERY);
    mq.addEventListener("change", notify);
    return () => mq.removeEventListener("change", notify);
}

function getReduceMotion(): boolean {
    return window.matchMedia(REDUCE_MOTION_QUERY).matches;
}

export default function Sn4peCallout() {
    const sectionRef = useRef<HTMLElement>(null);
    const reduceMotion = useSyncExternalStore(
        subscribeReduceMotion,
        getReduceMotion,
        () => false,
    );
    const [progress, setProgress] = useState(reduceMotion ? 1 : 0);
    const [prevReduce, setPrevReduce] = useState(reduceMotion);
    if (prevReduce !== reduceMotion) {
        setPrevReduce(reduceMotion);
        if (reduceMotion) setProgress(1);
    }
    const t = useTranslations("sn4peCallout");

    useEffect(() => {
        if (reduceMotion) return;
        const section = sectionRef.current;
        if (!section) return;

        const update = () => {
            const r = section.getBoundingClientRect();
            const vh = window.innerHeight;
            const target = vh * 0.55;
            const p = (vh - r.top) / Math.max(1, vh - target);
            setProgress(Math.max(0, Math.min(1, p)));
        };

        const raf = requestAnimationFrame(update);
        window.addEventListener("scroll", update, { passive: true });
        window.addEventListener("resize", update);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("scroll", update);
            window.removeEventListener("resize", update);
        };
    }, [reduceMotion]);

    const letters = TEXT.split("");

    return (
        <section
            ref={sectionRef}
            className="relative border-y border-dashed border-border py-16 sm:py-20"
        >
            <a
                href={SN4PE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group block"
            >
                <span className="block font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                    {t("tag")}
                </span>

                <span
                    className="mt-3 flex items-baseline gap-3"
                    style={{ perspective: "800px" }}
                >
                    <span className="text-5xl font-medium tracking-tight sm:text-6xl">
                        {letters.map((ch, i) => {
                            const stagger = i * 0.045;
                            const lp = Math.max(
                                0,
                                Math.min(1, (progress - stagger) * 1.7),
                            );
                            const inv = 1 - lp;
                            const dx = (rand(i, 1) - 0.5) * 280 * inv;
                            const dy = (rand(i, 2) - 0.5) * 140 * inv;
                            const rot = (rand(i, 3) - 0.5) * 90 * inv;
                            const blur = inv * 6;
                            const op = 0.05 + lp * 0.95;
                            const muted = i >= 5;
                            return (
                                <span
                                    key={i}
                                    className={`inline-block will-change-transform ${
                                        muted ? "text-muted-foreground" : "text-foreground"
                                    }`}
                                    style={{
                                        transform: `translate3d(${dx}px, ${dy}px, 0) rotate(${rot}deg)`,
                                        opacity: op,
                                        filter: blur > 0.1 ? `blur(${blur}px)` : "none",
                                    }}
                                >
                                    {ch}
                                </span>
                            );
                        })}
                    </span>
                    <span
                        aria-hidden
                        className="font-mono text-2xl text-muted-foreground transition-transform duration-300 group-hover:translate-x-2 group-hover:text-foreground sm:text-3xl"
                        style={{ opacity: Math.max(0, progress * 1.2 - 0.2) }}
                    >
                        ↗
                    </span>
                </span>

                <p
                    className="mt-5 max-w-prose text-sm text-muted-foreground"
                    style={{ opacity: Math.max(0, progress * 1.4 - 0.4) }}
                >
                    {t("subtitle")}
                </p>

                <p
                    className="mt-4 font-mono text-[11px] uppercase tracking-wide text-muted-foreground"
                    style={{ opacity: Math.max(0, progress * 1.6 - 0.6) }}
                >
                    {SECTIONS.join("  ·  ")}
                </p>
            </a>
        </section>
    );
}
